import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useState } from "react";
import { ArrowRight, Check, AlertTriangle, TrendingUp, Clock } from "lucide-react";
import { MarketingShell } from "@/components/marketing-shell";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";
import { cn } from "@/lib/utils";
import { PLANS, PLAN_ORDER, type PlanKey } from "@/lib/billing-plans";

export const Route = createFileRoute("/")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      {
        title:
          "Super Affiliate OS, The intelligence layer for serious affiliate marketers",
      },
      {
        name: "description",
        content:
          "Unify every affiliate program you promote, ClickFunnels, Skool, GoHighLevel, and every other platform, into one intelligence layer that catches false churns, missed commissions, and shows your real MRR. Built by a $1M+ super affiliate.",
      },
      {
        property: "og:title",
        content:
          "Super Affiliate OS, The intelligence layer for serious affiliate marketers",
      },
      {
        property: "og:description",
        content:
          "Catch what your affiliate dashboards miss. False churns, missed second-tier payouts, and your real MRR across every program you promote.",
      },
      { property: "og:url", content: "https://superaffiliateos.com/" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://superaffiliateos.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Super Affiliate OS",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: [
            {
              "@type": "Offer",
              name: "Starter Affiliate",
              price: "47",
              priceCurrency: "USD",
            },
            {
              "@type": "Offer",
              name: "Pro Affiliate",
              price: "97",
              priceCurrency: "USD",
            },
            {
              "@type": "Offer",
              name: "Super Affiliate",
              price: "197",
              priceCurrency: "USD",
            },
          ],
        }),
      },
    ],
  }),
  component: Landing,
});

/* ============================== HERO ============================== */

function HeroMockup() {
  const programs = [
    { name: "Prime Mover", value: "$18,420", tone: "bg-emerald-400" },
    { name: "ClickFunnels", value: "$11,200", tone: "bg-sky-400" },
    { name: "Skool", value: "$6,890", tone: "bg-violet-400" },
    { name: "GoHighLevel", value: "$3,450", tone: "bg-amber-400" },
    { name: "Marketing Secrets", value: "$2,934", tone: "bg-rose-400" },
  ];
  return (
    <div
      className="relative w-full rounded-2xl border border-border bg-card/60 p-4 shadow-2xl shadow-black/40 backdrop-blur-sm"
      style={{ transform: "perspective(1600px) rotateY(-3deg) rotateX(2deg)" }}
    >
      {/* Window chrome */}
      <div className="mb-3 flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span className="font-medium text-foreground">Command Center</span>
        <span>·</span>
        <span className="rounded-md border border-border bg-background/60 px-2 py-0.5">
          All Programs ▾
        </span>
        <span className="rounded-md border border-border bg-background/60 px-2 py-0.5">
          This Month ▾
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MetricTile
          label="Total Revenue"
          value="$42,894"
          delta={
            <span className="text-primary">
              <TrendingUp className="mr-1 inline h-3 w-3" />
              18% vs last month
            </span>
          }
        />
        <MetricTile
          label="MRR"
          value="$14,420"
          delta={
            <span className="text-muted-foreground">
              <span className="text-primary">▲ $891 new</span>
              <span className="mx-1">·</span>
              <span className="text-destructive">▼ $312 lost</span>
            </span>
          }
        />
        <MetricTile label="Active Customers" value="312" />
        <MetricTile label="Churn Rate" value="4.2%" />
      </div>

      <div className="mt-3 rounded-lg border border-border bg-background/60 p-3">
        <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          Program breakdown
        </div>
        <ul className="space-y-1.5">
          {programs.map((p) => (
            <li key={p.name} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2">
                <span className={cn("h-1.5 w-1.5 rounded-full", p.tone)} />
                <span className="text-foreground/90">{p.name}</span>
              </span>
              <span className="font-mono font-tnum text-foreground">{p.value}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
        <div>
          <span className="font-medium text-foreground">3 false churns detected</span>
          <span className="text-muted-foreground">, $891 in real revenue recovered</span>
        </div>
      </div>
    </div>
  );
}

function MetricTile({
  label,
  value,
  delta,
}: {
  label: string;
  value: string;
  delta?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-mono font-tnum text-lg text-foreground">{value}</div>
      {delta ? <div className="mt-0.5 text-[10px]">{delta}</div> : null}
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden">
      {/* Background mesh */}
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="marketing-mesh marketing-mesh-anim absolute -inset-32 opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
      </div>

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 pt-24 pb-24 lg:grid-cols-5 lg:pt-32 lg:pb-32 lg:min-h-[calc(100vh-4rem)]">
        <div className="lg:col-span-3 reveal">
          <div className="mb-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            14-day free trial · Card required · 30-day refund
          </div>
          <h1 className="text-balance text-5xl font-semibold tracking-tightest leading-[1.05] sm:text-6xl lg:text-[64px]">
            Your affiliate dashboards
            <br />
            <span className="text-muted-foreground">are lying to you.</span>
          </h1>
          <p className="mt-7 max-w-[60ch] text-lg leading-[1.6] text-muted-foreground">
            Super Affiliate OS is the intelligence layer above every program
            you promote, ClickFunnels, Skool, GoHighLevel, Prime Mover,
            Marketing Secrets, and every other platform in your stack, that
            catches what your dashboards miss. False churns. Missed
            second-tier payouts. Your real MRR across everything. Built by a
            super affiliate who's earned over $1M in commissions and got
            tired of accepting whatever the reports said.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button size="lg" asChild>
              <Link to="/signup">
                Start 14-day free trial <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="ghost" asChild>
              <Link to="/demo">See our interactive demo</Link>
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            No charge until day 15. Cancel anytime.
          </p>
        </div>
        <div className="lg:col-span-2 reveal" style={{ animationDelay: "120ms" }}>
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}

/* ============================ LOGO STRIP ============================ */

const LOGOS = [
  "ClickFunnels",
  "Skool",
  "GoHighLevel",
  "ActiveCampaign",
  "Kit",
  "Kajabi",
  "ThriveCart",
  "SamCart",
  "Teachable",
  "Podia",
  "Beehiiv",
  "Stripe",
  "Marketing Secrets",
  "Secrets of Success",
  "Prime Mover",
  "HighLevel",
  "Systeme.io",
  "Builderall",
  "Circle",
  "MemberPress",
];

function LogoStrip() {
  const doubled = [...LOGOS, ...LOGOS];
  return (
    <section className="border-y border-border/60 py-14">
      <div className="mx-auto max-w-7xl px-6">
        <p className="text-center text-xs uppercase tracking-wider text-muted-foreground">
          Works with every affiliate program you promote
        </p>
        <div className="group relative mt-8 overflow-hidden">
          <div
            className="flex w-max gap-10 whitespace-nowrap"
            style={{ animation: "marquee 60s linear infinite" }}
          >
            {doubled.map((name, i) => (
              <span
                key={`${name}-${i}`}
                className="text-base font-medium tracking-tight text-foreground/40 transition-opacity hover:text-foreground"
              >
                {name}
              </span>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent" />
        </div>
      </div>
      <style>{`
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .group:hover [style*="marquee"] { animation-play-state: paused !important; }
        @media (prefers-reduced-motion: reduce) {
          [style*="marquee"] { animation: none !important; }
        }
      `}</style>
    </section>
  );
}

/* =========================== THE PROBLEM =========================== */

function ProblemCard({
  visual,
  title,
  body,
}: {
  visual: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <article className="group relative flex flex-col gap-5 rounded-2xl border border-border bg-card/60 p-7 transition-all hover:border-foreground/20 hover:bg-card">
      <div className="h-24">{visual}</div>
      <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
      <p className="text-sm leading-[1.7] text-muted-foreground">{body}</p>
    </article>
  );
}

function Problem() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-28">
      <div className="text-center">
        <h2 className="text-4xl font-semibold tracking-tightest sm:text-5xl">
          Every affiliate marketer knows this story.
        </h2>
        <p className="mt-4 text-base text-muted-foreground">
          The infrastructure hasn't caught up to the work you're doing.
        </p>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <ProblemCard
          title="Your dashboards lie to you."
          visual={
            <div className="flex h-full items-end gap-1">
              {[40, 65, 30, 80, 50, 70, 35, 60].map((h, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex-1 rounded-sm",
                    i === 3 || i === 6
                      ? "bg-destructive/70"
                      : "bg-foreground/15",
                  )}
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          }
          body="Your trial churn list includes people who actually upgraded. Your active count contradicts your commission count. Your MRR isn't shown anywhere. Across ten years of running affiliate campaigns, I'm convinced this has cost me seven figures, and I don't think I'm alone."
        />
        <ProblemCard
          title="Your win-back emails go to people who already bought."
          visual={
            <div className="flex h-full flex-col justify-center gap-1.5">
              <div className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-1.5 text-[11px]">
                <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
                <span className="text-muted-foreground line-through">jenny@…</span>
                <span className="ml-auto text-destructive">churned</span>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-[11px]">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="text-foreground">jenny@…</span>
                <span className="ml-auto text-primary">platinum</span>
              </div>
            </div>
          }
          body={`The "churned" tag in your dashboard doesn't actually mean churned. Some of those people upgraded to your highest tier. They're now reading your "we'd love you back" email and quietly losing respect for you. Every time it happens, you don't find out.`}
        />
        <ProblemCard
          title="Your real numbers are in eight different spreadsheets."
          visual={
            <div className="flex h-full items-center justify-center">
              <div className="font-mono font-tnum text-5xl text-foreground/30">
                08<span className="text-primary">/</span>?
              </div>
            </div>
          }
          body="Every affiliate marketer eventually builds a spreadsheet ritual. Download eight CSVs from eight platforms. Copy-paste into tabs. De-dupe. Reconcile. Try to figure out what you actually made this month. Ship it to your CRM. Pray you didn't email the wrong list. There has to be a better way. There is now."
        />
        <ProblemCard
          title="Your launches eat your weekends."
          visual={
            <div className="flex h-full items-center justify-center gap-3">
              <div className="font-mono font-tnum text-5xl text-foreground/30">
                48<span className="text-primary">h</span>
              </div>
              <Clock className="h-8 w-8 text-foreground/30" />
            </div>
          }
          body="The big launches are supposed to be the wins. Instead they're 48-hour weekends of downloading reports from eight platforms, cross-referencing spreadsheets, segmenting trials and upgrades and cancellations, manually building CRM lists for bonus delivery, and chasing failed payments. Some of us have stopped doing it. Some of us have started questioning whether the commissions are even worth the labor. They are. The labor isn't."
        />
      </div>
    </section>
  );
}

/* =========================== FOUNDER NOTE =========================== */

function FounderNote() {
  return (
    <section className="relative border-y border-border/60 bg-card/30 py-28">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-px max-w-2xl bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      <div className="mx-auto max-w-[720px] px-6">
        <div className="text-center text-xs uppercase tracking-wider text-muted-foreground">
          Founder's Note
        </div>
        <h2 className="mt-4 text-center text-4xl font-semibold tracking-tightest sm:text-5xl">
          Why this exists.
        </h2>
        <div className="mt-10 space-y-5 text-[17px] leading-[1.75] text-foreground/85">
          <p>
            I've been running affiliate campaigns for over a decade. I've earned more than
            $1M in affiliate commissions across the major platforms, ClickFunnels,
            Prime Mover, Marketing Secrets, Skool, and every offer in between. And for
            most of that time, I just accepted whatever the affiliate dashboards told me.
          </p>
          <p>
            Not because I didn't suspect they were wrong. I did. Every serious affiliate
            marketer suspects it.
          </p>
          <p>
            But the alternative was line-by-line forensic accounting across eight
            platforms whose reports didn't agree with each other. Support tickets that
            took weeks to resolve and usually ended with "we'll look into it." Days of
            work to recover what might be a few hundred dollars. The math never quite
            worked out.
          </p>
          <p>So I did what most affiliates do. I accepted it. For ten years.</p>
          <p>
            But the labor cost was worse than the lost commissions. Every big launch
            turned into a 48-hour weekend of forensic work. Downloading eight reports.
            Cross-referencing spreadsheets. Segmenting trials, upgrades, cancellations.
            Manually building CRM lists so I could deliver bonuses to the right buyers
            and send win-back sequences to the right churners. The successful affiliates
            I knew were quietly burning out. Some had stopped running launches at all.
            Some had hired full-time ops people just to manage the spreadsheet ritual.
            I was somewhere in between, doing it myself and resenting it.
          </p>
          <p>
            I built Super Affiliate OS so I could stop. It runs the reconciliation. It
            segments the contacts. It pushes clean lists to my CRM on autopilot. Bonus
            delivery fires the moment a sale clears. Win-back emails trigger off real
            signal, not last week's stale data. Now I focus on promoting. The
            infrastructure runs 24/7/365 without me touching it.
          </p>
          <p>
            Looking back, I think the unreported losses, the false churns, the missed
            second-tier payouts, the trial conversions that never got credited, the
            commissions that quietly disappeared into platform discrepancies, add up to
            something north of seven figures. Not for one company. Across all of them,
            across a decade.
          </p>
          <p>
            That's not a problem with any one platform. That's an infrastructure problem
            with our entire industry.
          </p>
          <p>
            Affiliate marketers are some of the highest-leverage operators in business.
            We move billions of dollars in revenue for the companies whose tools we
            promote. We're the ones who actually drive new customers in a market where
            paid acquisition keeps getting more expensive. And our infrastructure, the
            dashboards, the reports, the CRM handoffs, the bonus delivery, the win-back
            sequences, is held together with spreadsheets and faith.
          </p>
          <p className="font-medium text-foreground">
            Super Affiliate OS is what I built to stop accepting it.
          </p>
          <p>
            One intelligence layer above every affiliate program you promote. It reads
            your reports across every platform. It catches the contradictions, the
            false churns, the trial-to-paid conversions that didn't get credited, the
            second-tier commissions that look weird. It tracks your customers as humans
            across every program they're in, so you finally know your real LTV. It
            pushes clean data to your CRM so your audiences are correct and your
            sequences stop embarrassing you.
          </p>
          <p>
            If you've been in this game long enough to know what I'm talking about, you
            already know if this is for you.
          </p>
        </div>
        <div className="mt-12 flex flex-col items-center gap-4">
          <div className="h-px w-16 bg-border" />
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
           , Built by a super affiliate, for super affiliates.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================= FEATURES ============================= */

function FeatureCard({
  title,
  body,
  visual,
  span = "",
}: {
  title: string;
  body: string;
  visual: React.ReactNode;
  span?: string;
}) {
  return (
    <article
      className={cn(
        "group relative flex flex-col gap-5 rounded-2xl border border-border bg-card/60 p-7 transition-all hover:border-foreground/20",
        span,
      )}
    >
      <div className="h-28 overflow-hidden">{visual}</div>
      <div>
        <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
        <p className="mt-3 text-sm leading-[1.7] text-muted-foreground">{body}</p>
      </div>
    </article>
  );
}

function Features() {
  return (
    <section id="features" className="mx-auto max-w-7xl px-6 py-28">
      <div className="text-center">
        <h2 className="text-4xl font-semibold tracking-tightest sm:text-5xl">
          Built for the way real affiliates actually work.
        </h2>
        <p className="mt-4 text-base text-muted-foreground">
          Six tools, working together as one operating system.
        </p>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <FeatureCard
          title="One workspace per program."
          body="Every affiliate platform you promote gets its own home. ClickFunnels has its workspace. Skool has its workspace. Prime Mover has its workspace. Each one knows its own commission structure, its own products, its own segmentation rules. They don't fight with each other. They just work."
          visual={
            <div className="relative h-full">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute h-20 w-32 rounded-lg border border-border bg-background/80"
                  style={{ left: `${i * 22}px`, top: `${i * 8}px`, zIndex: 3 - i }}
                >
                  <div className="px-3 py-2 text-[10px] text-muted-foreground">
                    {["ClickFunnels", "Skool", "Prime Mover"][i]}
                  </div>
                  <div className="mx-3 h-1.5 w-12 rounded bg-foreground/15" />
                  <div className="mx-3 mt-1 h-1.5 w-20 rounded bg-foreground/10" />
                </div>
              ))}
            </div>
          }
        />
        <FeatureCard
          title="Command Center across all of them."
          body="Above your workspaces, one view that aggregates everything. Total MRR. Total revenue this month. True churn rate. Real active customer count. Every metric calculated correctly across every program you run."
          visual={
            <div className="grid h-full grid-cols-3 gap-2">
              {[
                { l: "MRR", v: "$14,420" },
                { l: "Revenue", v: "$42,894" },
                { l: "Churn", v: "4.2%" },
              ].map((m) => (
                <div key={m.l} className="rounded-md border border-border bg-background/60 p-2">
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
                    {m.l}
                  </div>
                  <div className="mt-1 font-mono font-tnum text-sm">{m.v}</div>
                </div>
              ))}
            </div>
          }
        />
        <FeatureCard
          title="AI that gets sharper every month."
          body={`Trial marked "churned" but they actually upgraded? Caught. Second-tier commission listed at the wrong rate? Caught. Same customer counted twice across two programs? Caught. And because the system learns from every affiliate using it, the classifications get sharper across the platform, when two operators agree on how a product should be categorized, every future import benefits. Every contradiction surfaces in one inbox you review when you want to.`}
          visual={
            <div className="flex h-full flex-col justify-center">
              <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-xs">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-warning" />
                <div>
                  <div className="font-medium text-foreground">3 false churns detected</div>
                  <div className="text-[11px] text-muted-foreground">
                    $891 in revenue to recover
                  </div>
                </div>
              </div>
            </div>
          }
        />
        <FeatureCard
          title="People view across every program."
          body="Your customers aren't rows in different exports. They're humans, and most of them buy from you across multiple programs. See each one with their cross-program LTV, every product they've bought, every program they're active in, and their churn risk score."
          visual={
            <div className="flex h-full items-center gap-3 rounded-md border border-border bg-background/60 p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 font-mono text-sm text-primary">
                JD
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground">Jenny Diaz</div>
                <div className="text-[11px] text-muted-foreground">
                  4 programs · LTV{" "}
                  <span className="font-mono text-foreground">$8,420</span>
                </div>
              </div>
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] text-primary">
                low risk
              </span>
            </div>
          }
        />
        <FeatureCard
          title="Reports run on autopilot."
          body="Active trials. New buyers. Cancelled subscribers. Win-back targets. Failed payments. Each segment pushed to your CRM on whatever cadence you want, every hour, every day, your call. Bonus delivery fires the moment a sale clears. Win-back sequences trigger off real signal. You haven't manually built a CRM list in months."
          visual={
            <div className="flex h-full flex-col justify-center gap-1.5">
              {[
                { label: "trials → ActiveCampaign", t: "2m ago" },
                { label: "winback → Kit", t: "14m ago" },
                { label: "buyers → GoHighLevel", t: "1h ago" },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-[11px]"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="text-foreground/90">{row.label}</span>
                  <span className="ml-auto font-mono text-muted-foreground">{row.t}</span>
                </div>
              ))}
            </div>
          }
        />
        <FeatureCard
          title="CRM-ready exports for every segment."
          body="Active trials. Platinum buyers. Cancelled subscribers. Win-back targets. Each one, one click, clean CSV ready to drop into ActiveCampaign, Kit, GoHighLevel, or whatever you run. No more manual de-duping. No more emailing the wrong list."
          visual={
            <div className="flex h-full items-center gap-2">
              {["trials", "platinum", "winback"].map((t, i) => (
                <div
                  key={t}
                  className="flex-1 rounded-md border border-border bg-background/60 p-2 text-center"
                  style={{ transform: `translateY(${i * 2}px)` }}
                >
                  <div className="mx-auto h-6 w-6 rounded bg-primary/20" />
                  <div className="mt-1 font-mono text-[9px] text-muted-foreground">
                    {t}.csv
                  </div>
                </div>
              ))}
            </div>
          }
        />
      </div>
    </section>
  );
}

/* =========================== HOW IT WORKS =========================== */

function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Connect your programs.",
      body: "Pick from the template library, ClickFunnels, Skool, GoHighLevel, every major platform pre-configured, or create custom workspaces for programs we haven't templated yet. Setup takes three minutes per program.",
    },
    {
      n: "02",
      title: "Connect and forget.",
      body: "Our intelligence layer connects to every program you promote and pulls your reports automatically. Real MRR. True churn. Active customer counts that match your commission statements. Pushed through to your CRM on whatever cadence you want... every hour, every day, your call. Bonus delivery fires on autopilot. Win-back sequences trigger off real signal, not stale data. You stop touching CSVs.",
    },
    {
      n: "03",
      title: "See your real numbers.",
      body: "Real MRR. Real churn. Real LTV. The contradictions surface as anomalies you can confirm with one click. Clean segments push to your CRM. Welcome to running an operation, not a spreadsheet.",
    },
  ];
  return (
    <section id="how" className="border-y border-border/60 bg-card/20 py-28">
      <div className="mx-auto max-w-7xl px-6">
        <h2 className="text-center text-4xl font-semibold tracking-tightest sm:text-5xl">
          Set up once. Run forever.
        </h2>
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="flex flex-col gap-4">
              <div className="font-mono font-tnum text-4xl text-primary">{s.n}</div>
              <h3 className="text-xl font-semibold tracking-tight">{s.title}</h3>
              <p className="text-sm leading-[1.7] text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-14 text-center text-sm text-muted-foreground">
          Coming soon: automatic dashboard sync, so you don't even need to download the CSVs.
        </p>
      </div>
    </section>
  );
}

/* ============================= PRICING ============================= */

function Pricing() {
  const [annual, setAnnual] = useState(false);
  const INHERITS: Record<PlanKey, string | undefined> = {
    starter: undefined,
    pro: "Everything in Starter, plus:",
    super: "Everything in Pro, plus:",
  };
  const plans = PLAN_ORDER.map((key) => {
    const def = PLANS[key];
    // Strip the leading "Everything in …" line from features (shown separately as `inherits`).
    const features = def.features.filter((f) => !f.startsWith("Everything in "));
    return {
      key,
      name: def.name,
      monthly: def.display.monthly,
      annual: def.display.annual,
      tagline: def.tagline + ".",
      features,
      highlight: !!def.popular,
      inherits: INHERITS[key],
    };
  });

  return (
    <section id="pricing" className="mx-auto max-w-7xl px-6 py-28">
      <div className="text-center">
        <h2 className="text-4xl font-semibold tracking-tightest sm:text-5xl">
          Pick the size that matches your stack.
        </h2>
        <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-border bg-card/60 p-1 text-sm">
          <button
            type="button"
            onClick={() => setAnnual(false)}
            className={cn(
              "rounded-full px-4 py-1.5 transition-colors",
              !annual ? "bg-foreground text-background" : "text-muted-foreground",
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setAnnual(true)}
            className={cn(
              "rounded-full px-4 py-1.5 transition-colors",
              annual ? "bg-foreground text-background" : "text-muted-foreground",
            )}
          >
            Annual
          </button>
        </div>
        {annual && (
          <div className="mt-3 text-xs text-primary">
            Save up to $1,397/year on annual.
          </div>
        )}
      </div>
      <div className="mt-12 grid items-stretch gap-6 md:grid-cols-3">
        {plans.map((p) => (
          <div
            key={p.name}
            className={cn(
              "relative flex flex-col rounded-2xl border bg-card/60 p-7 transition-all hover:border-foreground/30",
              p.highlight
                ? "border-primary/60 shadow-[0_0_60px_-20px] shadow-primary/50 md:scale-[1.02]"
                : "border-border",
            )}
          >
            {p.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-primary-foreground">
                Most popular
              </div>
            )}
            <div className="text-base font-semibold tracking-tight">{p.name}</div>
            <div className="mt-1 text-xs text-muted-foreground">{p.tagline}</div>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-mono font-tnum text-4xl text-foreground">
                ${(annual ? p.annual.amount : p.monthly.amount).toLocaleString()}
              </span>
              <span className="text-sm text-muted-foreground">
                {annual ? p.annual.suffix : p.monthly.suffix}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {annual ? "14-day free trial, cancel anytime" : "14-day free trial, cancel anytime"}
            </div>
            <Button className="mt-6" variant={p.highlight ? "default" : "outline"} asChild>
              <Link to="/signup">Start free trial</Link>
            </Button>
            {p.inherits && (
              <div className="mt-6 text-xs font-medium text-foreground/80">{p.inherits}</div>
            )}
            <ul className="mt-4 space-y-2.5 text-sm">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-muted-foreground">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="mt-10 text-center text-sm text-muted-foreground">
        14 days free. Card required. 30-day refund. No questions asked.
      </p>
    </section>
  );
}

/* =============================== FAQ =============================== */

const FAQ_ITEMS = [
  {
    q: "Which affiliate programs do you support?",
    a: "Anything you can export as a CSV. We have pre-built templates for ClickFunnels, Skool, GoHighLevel, ActiveCampaign, Kit, Kajabi, ThriveCart, SamCart, Teachable, Beehiiv, Marketing Secrets, Secrets of Success, Prime Mover, and growing. For programs without templates, our AI maps your CSV columns automatically and saves the mapping for next time. New templates are added by the community and reviewed weekly.",
  },
  {
    q: "Do you connect directly to my affiliate dashboards?",
    a: "Not yet. Right now you upload CSV exports manually (about two minutes per program, once per week or whenever you want fresh data). Automatic dashboard sync is in v2, coming in 2026, Super Affiliate tier gets early access.",
  },
  {
    q: "How does the AI mapper work?",
    a: "When you upload a CSV, the AI looks at your column headers and first few rows, figures out which columns mean what (email, status, commission amount, etc.), and proposes a mapping. You confirm or adjust. Once saved, every future upload from that program uses the same mapping automatically.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "Your data stays. If you cancel, your account moves to a paused state, login blocked, data preserved. When you re-subscribe, everything is intact. We don't believe in punishing customers for leaving. (We also offer 30-day refunds, so if you don't love it, you get your money back, no questions.)",
  },
  {
    q: "Can my team use this with me?",
    a: "Pro Affiliate includes one teammate, Super Affiliate includes three. Each teammate gets their own login with permissions you control, full admin, manager (can upload and push to CRM but not change workspace settings), or viewer (read-only). Team activity is logged so you always know who did what.",
  },
  {
    q: "Is this for solo affiliates or agencies?",
    a: "Both. Solo affiliates use it to unify their own stack. Agencies and consultants use it to manage client affiliate operations. The architecture is the same; the team seats are different.",
  },
  {
    q: "What if I run out of workspaces on my plan?",
    a: "Upgrade anytime, instantly. You keep all your existing data, just unlock more workspaces and features. Downgrade also works, if you go down a tier, workspaces past the limit go into a read-only state but aren't deleted.",
  },
  {
    q: "Who's behind this?",
    a: "Built by a super affiliate who's earned over $1M in commissions across a decade of running campaigns on ClickFunnels, Prime Mover, Skool, GoHighLevel, and the rest of the major platforms. We don't claim to be neutral observers of the affiliate industry, we're affiliates. This is the tool we wished existed.",
  },
];

function Faq() {
  return (
    <section id="faq" className="border-t border-border/60 py-28">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-4xl font-semibold tracking-tightest sm:text-5xl">
          Frequently asked.
        </h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-border">
              <AccordionTrigger className="text-left text-base font-medium tracking-tight hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-[1.7] text-muted-foreground">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ============================ FINAL CTA ============================ */

function FinalCta() {
  return (
    <section className="relative isolate overflow-hidden border-t border-border/60 py-32">
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="marketing-mesh absolute -inset-32 opacity-40" />
      </div>
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-balance text-4xl font-semibold tracking-tightest sm:text-5xl">
          Stop running launches like it's 2015.
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-base leading-[1.7] text-muted-foreground">
          If our intelligence layer doesn't find at least one anomaly that costs you real
          money, a false churn, a missed second-tier payout, a duplicate contact, AND
          save you at least 10 hours of manual reporting work in your first 30 days,
          refund within 30 days, no questions. We've never had to run that experiment.
        </p>
        <div className="mt-10">
          <Button size="lg" asChild>
            <Link to="/signup">
              Start 14-day free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Card required. No charge until day 15. Cancel anytime.
        </p>
      </div>
    </section>
  );
}

/* ============================= DEMO CTA ============================= */

function DemoCta() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-20">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-card/60 p-10 sm:p-14">
        <div className="absolute inset-0 -z-10 opacity-60 marketing-mesh" />
        <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-wider text-primary">
              Interactive demo
            </div>
            <h2 className="mt-3 text-3xl font-semibold tracking-tightest sm:text-4xl">
              See the whole product. No signup, no friction.
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Click through a fully populated Super Affiliate OS, four workspaces, real-looking revenue, the Command Center, People view, Anomalies, Calendar, Bonus Builder, all of it. Read-only, no account needed.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button size="lg" asChild>
              <Link to="/demo">
                Open the demo <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/signup">Start free trial</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================= PAGE ============================= */

function Landing() {
  return (
    <MarketingShell>
      <Hero />
      <LogoStrip />
      <Problem />
      <FounderNote />
      <Features />
      <HowItWorks />
      <DemoCta />
      <Pricing />
      <Faq />
      <FinalCta />
    </MarketingShell>
  );
}
